<!--
    Component that combines Axios errors (from the vuex store) with Inertia errors
    (from $page.props) and displays as an alert
-->

<script lang="ts">
import { isAxiosError } from 'axios';

interface FieldError {
    fieldname: string;
    message: string;
}

/**
 * Recursively Get the messages for a value, which could be an array, an object,
 * a string, or an error, returning an array of {fieldname, message} objects
 *
 * @param value The value to extract messages from
 * @param fieldname The field the value belongs to
 * @returns An array of {fieldname, message} objects
 */
export function getErrors(value: unknown, fieldname = '_generic'): FieldError[] {
    if (typeof value === 'string') {
        return [{
            fieldname,
            message: value,
        }];
    }

    if (Array.isArray(value)) {
        // recurse into each item in the array
        return value.flatMap((item) => getErrors(item, fieldname));
    }

    if (value === null || value === undefined) {
        return [];
    }

    if (!(typeof value === 'object')) {
        console.log('Unexpected value passed to ErrorDisplay', value);
        return [];
    }

    // At this point we're sure its an object of some kind

    if (isAxiosError(value) && value.response && (value.response.status === 422)) {
        // It's specifically a Laravel validation error
        return getErrors(value.response.data.errors);
    }

    if (value instanceof Error && value.message) {
        // It's some random HTTP error
        return getErrors(value.message, fieldname);
    }

    // We'll assume it's some object of {fieldname: value, fieldname: value}
    return Object.entries(value)
        .flatMap(([f, v]) => getErrors(v, f));
}
</script>


<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
    value: unknown,
}>();

/**
 * The errors we're going to display, sorted by the visible message so it
 * doesn't seem random
 */
const errors = computed(() => getErrors(props.value)
    .sort((a, b) => a.message.localeCompare(b.message)));
</script>


<template>
    <div
        v-if="errors.length > 0"
        class="alert alert-danger"
    >
        <ul class="mb-0">
            <li
                v-for="({ message }, index ) in errors"
                :key="index"
            >
                <!--
                FIXME: ideally we'd do an internal link to #errors-{fieldname}, but inertia seems
                to want to reload the page rather than just scroll down to the right id.  Something
                like:

                <a v-if="!fieldname.startsWith('_')"
                    class="alert-link"
                    :href="`errors-${fieldname}`">{{ message }}</a>
                <strong v-else>{{ message }}</strong>
                -->
                <strong>{{ message }}</strong>
            </li>
        </ul>
    </div>
</template>
